import "./ContentCalendarView.css";

import CreateContentCalendarItemDialog from "./CreateContentCalendarItemDialog";
import MarkPreparationReadyAction from "./MarkPreparationReadyAction";

import CreateArticleDraftAction from "./CreateArticleDraftAction";

import {
  AlertTriangle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  Clock3,
  FileText,
  Filter,
  Plus,
  RefreshCw,
  Search,
  UserRound,
} from "lucide-react";

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  CONTENT_CALENDAR_PRIORITIES,
  CONTENT_CALENDAR_STATUSES,
  fetchContentCalendarItem,
  fetchContentCalendarItems,
  type ContentCalendarItem,
  type ContentCalendarPriority,
  type ContentCalendarStatus,
} from "../lib/contentCalendar";

interface ContentCalendarViewProps {
  onBack:
    () => void;
}

type StatusFilter =
  ContentCalendarStatus |
  "";

type PriorityFilter =
  ContentCalendarPriority |
  "";

const statusLabels:
  Record<
    ContentCalendarStatus,
    string
  > = {
    planned:
      "Planned",

    preparation_ready:
      "Preparation ready",

    article_linked:
      "Article linked",

    skipped:
      "Skipped",

    cancelled:
      "Cancelled",
  };

const priorityLabels:
  Record<
    ContentCalendarPriority,
    string
  > = {
    low:
      "Low",

    normal:
      "Normal",

    high:
      "High",

    urgent:
      "Urgent",
  };

function formatDateTime(
  value: string,
): string {
  const date =
    new Date(
      value,
    );

  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "Unknown date";
  }

  return new Intl.DateTimeFormat(
    "en-CA",
    {
      year:
        "numeric",

      month:
        "short",

      day:
        "numeric",

      hour:
        "2-digit",

      minute:
        "2-digit",
    },
  ).format(
    date,
  );
}

function ContentCalendarView({
  onBack,
}: ContentCalendarViewProps) {
  const [
    items,
    setItems,
  ] =
    useState<
      ContentCalendarItem[]
    >(
      [],
    );

  const [
    statusFilter,
    setStatusFilter,
  ] =
    useState<StatusFilter>(
      "",
    );

  const [
    priorityFilter,
    setPriorityFilter,
  ] =
    useState<PriorityFilter>(
      "",
    );

  const [
    searchTerm,
    setSearchTerm,
  ] =
    useState(
      "",
    );

  const [
    isCreateDialogOpen,
    setIsCreateDialogOpen,
  ] =
    useState(
      false,
    );

  const [
    selectedItemId,
    setSelectedItemId,
  ] =
    useState<
      string |
      null
    >(
      null,
    );

  const [
    selectedItem,
    setSelectedItem,
  ] =
    useState<
      ContentCalendarItem |
      null
    >(
      null,
    );

  const [
    isLoading,
    setIsLoading,
  ] =
    useState(
      true,
    );

  const [
    isDetailLoading,
    setIsDetailLoading,
  ] =
    useState(
      false,
    );

  const [
    error,
    setError,
  ] =
    useState<
      string |
      null
    >(
      null,
    );

  const [
    detailError,
    setDetailError,
  ] =
    useState<
      string |
      null
    >(
      null,
    );

  const loadItems =
    useCallback(
      async (
        signal?: AbortSignal,
      ) => {
        setIsLoading(
          true,
        );

        setError(
          null,
        );

        try {
          const response =
            await fetchContentCalendarItems(
              {
                status:
                  statusFilter ||
                  undefined,

                priority:
                  priorityFilter ||
                  undefined,

                limit:
                  200,
              },
              signal,
            );

          setItems(
            response.calendarItems,
          );
        } catch (
          loadError
        ) {
          if (
            signal?.aborted
          ) {
            return;
          }

          setError(
            loadError instanceof
              Error
              ? loadError.message
              : "The content calendar could not be loaded.",
          );
        } finally {
          if (
            !signal?.aborted
          ) {
            setIsLoading(
              false,
            );
          }
        }
      },
      [
        priorityFilter,
        statusFilter,
      ],
    );

  useEffect(
    () => {
      const controller =
        new AbortController();

      void loadItems(
        controller.signal,
      );

      return () => {
        controller.abort();
      };
    },
    [
      loadItems,
    ],
  );

  const filteredItems =
    useMemo(
      () => {
        const normalizedSearch =
          searchTerm
            .trim()
            .toLowerCase();

        if (
          !normalizedSearch
        ) {
          return items;
        }

        return items.filter(
          (
            item,
          ) =>
            item.topic
              .toLowerCase()
              .includes(
                normalizedSearch,
              ) ||
            item.workingTitle
              .toLowerCase()
              .includes(
                normalizedSearch,
              ) ||
            item.contentCategory
              .toLowerCase()
              .includes(
                normalizedSearch,
              ) ||
            (
              item.assignedAuthor ??
              ""
            )
              .toLowerCase()
              .includes(
                normalizedSearch,
              ),
        );
      },
      [
        items,
        searchTerm,
      ],
    );

  const openItem =
    useCallback(
      async (
        id: string,
      ) => {
        setSelectedItemId(
          id,
        );

        setSelectedItem(
          null,
        );

        setDetailError(
          null,
        );

        setIsDetailLoading(
          true,
        );

        try {
          const response =
            await fetchContentCalendarItem(
              id,
            );

          setSelectedItem(
            response.calendarItem,
          );
        } catch (
          itemError
        ) {
          setDetailError(
            itemError instanceof
              Error
              ? itemError.message
              : "The calendar item could not be loaded.",
          );
        } finally {
          setIsDetailLoading(
            false,
          );
        }
      },
      [],
    );

  return (
    <section className="calendar-view">
      <header className="calendar-page-header">
        <div>
          <button
            type="button"
            className="calendar-back-button"
            onClick={onBack}
          >
            <ArrowLeft
              aria-hidden
              size={16}
              strokeWidth={1.9}
            />

            Operations overview
          </button>

          <span className="section-eyebrow">
            Editorial planning
          </span>

          <h1>
            Content Calendar
          </h1>

          <p>
            Review upcoming topics, publication targets, ownership and
            production readiness.
          </p>
        </div>

        <div className="calendar-header-actions">
          <button
            type="button"
            className="primary-button"
            onClick={() => {
              setIsCreateDialogOpen(
                true,
              );
            }}
          >
            <Plus
              aria-hidden
              size={16}
              strokeWidth={1.9}
            />

            New calendar item
          </button>

          <span className="calendar-live-badge">
            <span className="online-indicator" />

            Live production data
          </span>

          <button
            type="button"
            className="secondary-button"
            disabled={isLoading}
            onClick={() => {
              void loadItems();
            }}
          >
            <RefreshCw
              aria-hidden
              size={16}
              strokeWidth={1.9}
              className={
                isLoading
                  ? "is-spinning"
                  : undefined
              }
            />

            Refresh
          </button>
        </div>
      </header>

      <section className="calendar-toolbar">
        <label className="calendar-search">
          <Search
            aria-hidden
            size={17}
            strokeWidth={1.8}
          />

          <input
            type="search"
            value={searchTerm}
            placeholder="Search topics, titles or authors"
            onChange={(
              event,
            ) => {
              setSearchTerm(
                event.target.value,
              );
            }}
          />
        </label>

        <label className="calendar-filter">
          <Filter
            aria-hidden
            size={16}
            strokeWidth={1.8}
          />

          <select
            value={statusFilter}
            onChange={(
              event,
            ) => {
              setStatusFilter(
                event.target.value as StatusFilter,
              );
            }}
          >
            <option value="">
              All statuses
            </option>

            {CONTENT_CALENDAR_STATUSES.map(
              (
                status,
              ) => (
                <option
                  key={status}
                  value={status}
                >
                  {statusLabels[
                    status
                  ]}
                </option>
              ),
            )}
          </select>
        </label>

        <label className="calendar-filter">
          <select
            value={priorityFilter}
            onChange={(
              event,
            ) => {
              setPriorityFilter(
                event.target.value as PriorityFilter,
              );
            }}
          >
            <option value="">
              All priorities
            </option>

            {CONTENT_CALENDAR_PRIORITIES.map(
              (
                priority,
              ) => (
                <option
                  key={priority}
                  value={priority}
                >
                  {priorityLabels[
                    priority
                  ]}
                </option>
              ),
            )}
          </select>
        </label>

        <div className="calendar-result-count">
          {filteredItems.length}
          {" "}
          {filteredItems.length ===
          1
            ? "item"
            : "items"}
        </div>
      </section>

      <div className="calendar-layout">
        <section className="calendar-list-panel">
          <div className="calendar-list-header">
            <span>
              Topic and working title
            </span>

            <span>
              Target publication
            </span>

            <span>
              Priority
            </span>

            <span>
              Status
            </span>

            <span className="sr-only">
              Open
            </span>
          </div>

          {isLoading ? (
            <div className="calendar-state">
              <RefreshCw
                aria-hidden
                size={25}
                strokeWidth={1.8}
                className="is-spinning"
              />

              <h2>
                Loading calendar
              </h2>

              <p>
                Synchronizing current production planning records.
              </p>
            </div>
          ) : error ? (
            <div className="calendar-state calendar-state-error">
              <AlertTriangle
                aria-hidden
                size={25}
                strokeWidth={1.8}
              />

              <h2>
                Calendar unavailable
              </h2>

              <p>
                {error}
              </p>

              <button
                type="button"
                className="primary-button"
                onClick={() => {
                  void loadItems();
                }}
              >
                Retry connection
              </button>
            </div>
          ) : filteredItems.length ===
            0 ? (
            <div className="calendar-state">
              <CalendarDays
                aria-hidden
                size={27}
                strokeWidth={1.7}
              />

              <h2>
                No matching calendar items
              </h2>

              <p>
                Change the search or filters to view more production
                records.
              </p>
            </div>
          ) : (
            <div className="calendar-list">
              {filteredItems.map(
                (
                  item,
                ) => (
                  <button
                    key={item.id}
                    type="button"
                    className={
                      selectedItemId ===
                      item.id
                        ? "calendar-row calendar-row-selected"
                        : "calendar-row"
                    }
                    onClick={() => {
                      void openItem(
                        item.id,
                      );
                    }}
                  >
                    <span className="calendar-topic-cell">
                      <strong>
                        {item.workingTitle}
                      </strong>

                      <small>
                        {item.topic}
                        {" · "}
                        {item.contentCategory}
                      </small>
                    </span>

                    <span className="calendar-date-cell">
                      <Clock3
                        aria-hidden
                        size={15}
                        strokeWidth={1.8}
                      />

                      {formatDateTime(
                        item.targetPublishAt,
                      )}
                    </span>

                    <span
                      className="calendar-priority"
                      data-priority={item.priority}
                    >
                      {priorityLabels[
                        item.priority
                      ]}
                    </span>

                    <span
                      className="calendar-status"
                      data-status={item.status}
                    >
                      {statusLabels[
                        item.status
                      ]}
                    </span>

                    <ChevronRight
                      aria-hidden
                      size={17}
                      strokeWidth={1.8}
                    />
                  </button>
                ),
              )}
            </div>
          )}
        </section>

        <aside className="calendar-detail-panel">
          {!selectedItemId ? (
            <div className="calendar-detail-empty">
              <FileText
                aria-hidden
                size={28}
                strokeWidth={1.6}
              />

              <h2>
                Select a calendar item
              </h2>

              <p>
                Open a record to inspect its complete planning brief.
              </p>
            </div>
          ) : isDetailLoading ? (
            <div className="calendar-detail-empty">
              <RefreshCw
                aria-hidden
                size={25}
                strokeWidth={1.8}
                className="is-spinning"
              />

              <h2>
                Loading item
              </h2>
            </div>
          ) : detailError ? (
            <div className="calendar-detail-empty calendar-state-error">
              <AlertTriangle
                aria-hidden
                size={25}
                strokeWidth={1.8}
              />

              <h2>
                Item unavailable
              </h2>

              <p>
                {detailError}
              </p>
            </div>
          ) : selectedItem ? (
            <div className="calendar-detail-content">
              <div className="calendar-detail-heading">
                <span
                  className="calendar-status"
                  data-status={selectedItem.status}
                >
                  {statusLabels[
                    selectedItem.status
                  ]}
                </span>

                <h2>
                  {selectedItem.workingTitle}
                </h2>

                <p>
                  {selectedItem.topic}
                </p>
              </div>

              <dl className="calendar-detail-grid">
                <div>
                  <dt>
                    Category
                  </dt>

                  <dd>
                    {selectedItem.contentCategory}
                  </dd>
                </div>

                <div>
                  <dt>
                    Priority
                  </dt>

                  <dd>
                    <span
                      className="calendar-priority"
                      data-priority={selectedItem.priority}
                    >
                      {priorityLabels[
                        selectedItem.priority
                      ]}
                    </span>
                  </dd>
                </div>

                <div>
                  <dt>
                    Publication target
                  </dt>

                  <dd>
                    {formatDateTime(
                      selectedItem.targetPublishAt,
                    )}
                  </dd>
                </div>

                <div>
                  <dt>
                    Assigned author
                  </dt>

                  <dd>
                    <UserRound
                      aria-hidden
                      size={14}
                      strokeWidth={1.8}
                    />

                    {selectedItem.assignedAuthor ??
                      "Unassigned"}
                  </dd>
                </div>
              </dl>

              <div className="calendar-detail-section">
                <h3>
                  Research notes
                </h3>

                <p>
                  {selectedItem.researchNotes ??
                    "No research notes have been added."}
                </p>
              </div>

              <div className="calendar-detail-section">
                <h3>
                  Preferred call to action
                </h3>

                <p>
                  {selectedItem.preferredCta ??
                    "No preferred CTA has been added."}
                </p>
              </div>

              <div className="calendar-detail-section">
                <h3>
                  Featured image requirements
                </h3>

                <p>
                  {selectedItem.featuredImageRequirements ??
                    "No image requirements have been added."}
                </p>
              </div>

              {selectedItem.articleId ? (
                <div className="calendar-linked-article">
                  <CheckCircle2
                    aria-hidden
                    size={17}
                    strokeWidth={1.9}
                  />

                  Linked article:
                  {" "}
                  <code>
                    {selectedItem.articleId}
                  </code>
                </div>
              ) : null}

              <MarkPreparationReadyAction
                calendarItem={selectedItem}
                onUpdated={(
                  updatedCalendarItem,
                ) => {
                  setSelectedItem(
                    updatedCalendarItem,
                  );

                  setItems(
                    (
                      currentItems,
                    ) =>
                      currentItems.map(
                        (
                          currentItem,
                        ) =>
                          currentItem.id ===
                          updatedCalendarItem.id
                            ? updatedCalendarItem
                            : currentItem,
                      ),
                  );

                  void loadItems();
                }}
              />

              <CreateArticleDraftAction
                calendarItem={selectedItem}
                onUpdated={(
                  updatedCalendarItem,
                ) => {
                  setSelectedItem(
                    updatedCalendarItem,
                  );

                  setItems(
                    (
                      currentItems,
                    ) =>
                      currentItems.map(
                        (
                          currentItem,
                        ) =>
                          currentItem.id ===
                          updatedCalendarItem.id
                            ? updatedCalendarItem
                            : currentItem,
                      ),
                  );

                  void loadItems();
                }}
              />
              <div className="calendar-detail-footer">
                Updated
                {" "}
                {formatDateTime(
                  selectedItem.updatedAt,
                )}
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      <CreateContentCalendarItemDialog
        open={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(
            false,
          );
        }}
        onCreated={(
          calendarItem,
        ) => {
          setIsCreateDialogOpen(
            false,
          );

          setSelectedItemId(
            calendarItem.id,
          );

          setSelectedItem(
            calendarItem,
          );

          void loadItems();
        }}
      />
    </section>
  );
}

export default ContentCalendarView;
